"use client";

import { Globe } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { letterGrade, scoreTone } from "~/lib/siteglue/agent-ready/results-groups";
import {
  AUDIT_DEMO_ARO_COUNT,
  AUDIT_DEMO_ARO_SLIDE,
  AUDIT_DEMO_CHANNELS,
  AUDIT_DEMO_CRO_COUNT,
  AUDIT_DEMO_CRO_SLIDE,
  AUDIT_DEMO_DOMAIN,
  AUDIT_DEMO_LOOP_MS,
  auditDemoCheckedCount,
  auditDemoCountTo,
  auditDemoReducedMotionElapsed,
  auditDemoResultReveal,
  auditDemoSceneAt,
  auditDemoSceneProgress,
  auditDemoShouldAutoplay,
  auditDemoTypedDomain,
} from "~/lib/siteglue/how-it-works/audit-demo";
import {
  HOMEPAGE_SCAN_CTA,
  RESULTS_STATUS,
  SCAN_CHANNELS_LABEL,
  SCAN_REPORT_ARO_HELP,
  SCAN_REPORT_ARO_TITLE,
  SCAN_REPORT_CRO_HELP,
  SCAN_REPORT_CRO_TITLE,
  SCAN_REPORT_AGENTS_TEST_HELP,
  SCAN_REPORT_AGENTS_TEST_TITLE,
  SCAN_REPORTS_LABEL,
  SCAN_WIZARD_BACK,
  SCAN_WIZARD_CONTINUE,
} from "~/lib/siteglue/landing-redesign/copy";
import { homepagePreviewSampleLabel, type HomepagePreviewSlide } from "~/lib/siteglue/landing-redesign/homepage-hero";
import { cn } from "~/lib/utils";

const TONE_TEXT = {
  ready: "text-emerald-600",
  partly: "text-amber-600",
  not: "text-red-600",
  empty: "text-slate-400",
} as const;

const TONE_BAR = {
  ready: "bg-emerald-500",
  partly: "bg-amber-500",
  not: "bg-red-500",
  empty: "bg-slate-300",
} as const;

const TONE_RESULT = {
  Verified: "text-emerald-700",
  Recommended: "text-amber-700",
} as const;

function Chrome({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
      <div className="flex items-center gap-1.5" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      </div>
      <p className="truncate text-xs font-medium text-slate-500">{title}</p>
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
        Sample
      </span>
    </div>
  );
}

function ChoiceCard({
  title,
  help,
  checked,
  plain = false,
}: {
  title: string;
  help: string;
  checked: boolean;
  plain?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 px-4 py-3.5",
        plain
          ? ""
          : checked
            ? "rounded-2xl border border-[#4f81bd] bg-[#4f81bd]/[0.06]"
            : "rounded-2xl border border-slate-200 bg-white",
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{help}</span>
      </span>
      <span
        className={cn(
          "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border",
          checked ? "border-[#4f81bd] bg-[#4f81bd] text-white" : "border-slate-300 bg-white",
        )}
        aria-hidden
      >
        {checked ? "✓" : ""}
      </span>
    </div>
  );
}

function UrlScene({ typed, ready }: { typed: string; ready: boolean }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-2">
      <div className="flex w-full max-w-xl flex-col gap-2 rounded-[1.6rem] border border-slate-200 bg-white p-1.5 shadow-[0_10px_40px_rgba(15,23,42,0.06)] sm:flex-row sm:items-center sm:rounded-full">
        <div className="flex min-w-0 flex-1 items-center gap-2 px-3">
          <Globe className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          <span className="min-w-0 flex-1 truncate py-2.5 text-base text-slate-900">
            {typed || <span className="text-slate-400">yourcompany.com</span>}
          </span>
        </div>
        <span
          className={cn(
            "inline-flex h-11 items-center justify-center rounded-full bg-[#4f81bd] px-5 text-sm font-semibold text-white",
            ready && "ring-2 ring-[#4f81bd]/30",
          )}
        >
          {HOMEPAGE_SCAN_CTA}
        </span>
      </div>
    </div>
  );
}

function LineCheck({ label, checked }: { label: string; checked: boolean }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-800">
      <span
        className={cn(
          "inline-flex h-4 w-4 items-center justify-center rounded border text-[10px]",
          checked ? "border-[#4f81bd] bg-[#4f81bd] text-white" : "border-slate-300 bg-white text-transparent",
        )}
        aria-hidden
      >
        ✓
      </span>
      {label}
    </span>
  );
}

function ReportsScene({ progress }: { progress: number }) {
  const channelsChecked = auditDemoCheckedCount(
    AUDIT_DEMO_CHANNELS.length,
    Math.min(1, Math.max(0, (progress - 0.2) * 1.6)),
  );
  return (
    <div>
      <p className="text-sm font-semibold text-slate-900">{SCAN_REPORTS_LABEL}</p>
      <div className="mt-3 space-y-2.5">
        <div className="rounded-2xl border border-[#4f81bd] bg-[#4f81bd]/[0.06]">
          <ChoiceCard title={SCAN_REPORT_ARO_TITLE} help={SCAN_REPORT_ARO_HELP} checked plain />
        </div>
        <div className="rounded-2xl border border-[#4f81bd] bg-[#4f81bd]/[0.06]">
          <ChoiceCard title={SCAN_REPORT_CRO_TITLE} help={SCAN_REPORT_CRO_HELP} checked plain />
          <div className="border-t border-[#4f81bd]/15 px-4 pb-4 pt-3">
            <p className="text-sm font-semibold text-slate-900">{SCAN_CHANNELS_LABEL}</p>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
              {AUDIT_DEMO_CHANNELS.map((channel, index) => (
                <LineCheck key={channel.id} label={channel.label} checked={index < channelsChecked} />
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white">
          <ChoiceCard title={SCAN_REPORT_AGENTS_TEST_TITLE} help={SCAN_REPORT_AGENTS_TEST_HELP} checked={false} plain />
        </div>
      </div>
      <div className="mt-4 flex justify-between">
        <span className="rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-800">
          {SCAN_WIZARD_BACK}
        </span>
        <span className="rounded-full bg-[#4f81bd] px-6 py-2.5 text-sm font-semibold text-white">
          {SCAN_WIZARD_CONTINUE}
        </span>
      </div>
    </div>
  );
}

function ScanScene({ progress }: { progress: number }) {
  const aroDone = auditDemoCountTo(AUDIT_DEMO_ARO_COUNT, Math.min(1, progress * 1.15));
  const croDone = auditDemoCountTo(AUDIT_DEMO_CRO_COUNT, Math.max(0, (progress - 0.12) / 0.88));
  return (
    <div className="flex h-full flex-col items-center justify-center px-4 text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        Scanning {AUDIT_DEMO_DOMAIN}
      </p>
      <p className="mt-2 text-2xl font-semibold text-slate-900">Running ARO and CRO audits</p>
      <div className="mt-6 w-full max-w-md space-y-4 text-left">
        <div>
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-800">ARO Audit</span>
            <span className="tabular-nums text-slate-500">
              {aroDone}/{AUDIT_DEMO_ARO_COUNT}
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-[#4f81bd]"
              style={{ width: `${Math.max(4, Math.round((aroDone / AUDIT_DEMO_ARO_COUNT) * 100))}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-800">CRO Audit</span>
            <span className="tabular-nums text-slate-500">
              {croDone}/{AUDIT_DEMO_CRO_COUNT}
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-[#4f81bd]"
              style={{ width: `${Math.max(4, Math.round((croDone / AUDIT_DEMO_CRO_COUNT) * 100))}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ slide, progress }: { slide: HomepagePreviewSlide; progress: number }) {
  const score = auditDemoCountTo(slide.score, progress);
  const tone = scoreTone(score);
  const grade = letterGrade(score);
  const visibleRows = auditDemoCheckedCount(slide.rows.length, Math.min(1, progress * 1.15));

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
            {slide.kicker}
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-800">{RESULTS_STATUS(score)}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{slide.summary}</p>
        </div>
        <div className="flex items-end gap-3">
          <p className="text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            {score}
            <span className="text-lg font-medium text-slate-400">/100</span>
          </p>
          <p className={`text-3xl font-semibold tracking-tight sm:text-4xl ${TONE_TEXT[tone]}`}>
            {grade}
          </p>
        </div>
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {slide.scoreLabel}
      </p>
      <div className="mt-3 space-y-3">
        {slide.rows.map((row, index) => {
          const visible = index < visibleRows;
          const width = visible ? auditDemoCountTo(row.score, Math.min(1, progress * 1.2)) : 0;
          return (
            <div
              key={row.id}
              className={cn(
                "rounded-xl border border-slate-100 px-3 py-3 transition-opacity duration-300",
                visible ? "opacity-100" : "opacity-30",
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">{row.label}</p>
                <span className="flex shrink-0 items-center gap-2">
                  <p className={`text-sm font-semibold ${TONE_TEXT[scoreTone(width)]}`}>
                    {width}/100
                  </p>
                  {visible ? (
                    <p className={`text-xs font-semibold ${TONE_RESULT[row.result]}`}>{row.result}</p>
                  ) : null}
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${TONE_BAR[scoreTone(width)]}`}
                  style={{ width: `${Math.max(width, visible ? 4 : 0)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

function ResultsScene({ elapsed }: { elapsed: number }) {
  const reveal = auditDemoResultReveal(elapsed);
  return (
    <div>
      <p className="mb-3 text-center text-xs font-medium text-slate-500">
        {homepagePreviewSampleLabel()}
      </p>
      <div className="grid gap-3 lg:grid-cols-2">
        <ResultCard slide={AUDIT_DEMO_ARO_SLIDE} progress={reveal.aro} />
        <ResultCard slide={AUDIT_DEMO_CRO_SLIDE} progress={reveal.cro} />
      </div>
    </div>
  );
}

export function AuditDemoAnimation({ className }: { className?: string }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const elapsedRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const sync = () => setPageVisible(document.visibilityState === "visible");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(Boolean(entry?.isIntersecting)),
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const autoplay = auditDemoShouldAutoplay({ reducedMotion, inView, pageVisible, paused });

  useEffect(() => {
    if (reducedMotion) {
      const frozen = auditDemoReducedMotionElapsed();
      elapsedRef.current = frozen;
      setElapsed(frozen);
      return;
    }
    if (!autoplay) return;
    const startedAt = performance.now() - elapsedRef.current;
    let frame = 0;
    const tick = (now: number) => {
      const next = (now - startedAt) % AUDIT_DEMO_LOOP_MS;
      elapsedRef.current = next;
      setElapsed(next);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [autoplay, reducedMotion]);

  const scene = auditDemoSceneAt(elapsed);
  const typed = auditDemoTypedDomain(elapsed);
  const urlReady = scene === "url" && typed === AUDIT_DEMO_DOMAIN;
  const chromeTitle =
    scene === "results"
      ? "Readiness Report · Preview"
      : scene === "scan"
        ? "SiteGlue Audit · Scanning"
        : "SiteGlue Audit";

  return (
    <div
      ref={rootRef}
      className={cn("w-full", className)}
      data-testid="hiw-audit-demo"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <p className="sr-only">
        Animated preview of a SiteGlue audit: enter a website, choose ARO and CRO, then see independent scores.
      </p>
      <div
        aria-hidden
        className="overflow-hidden rounded-[1.6rem] border border-slate-200 bg-slate-100 shadow-[0_18px_50px_rgba(15,23,42,0.12)]"
      >
        <Chrome title={chromeTitle} />
        <div className="bg-[#f7f6f3] p-3 sm:p-4">
          <div
            key={scene}
            className={cn("min-h-[22rem] animate-fade-in", scene === "results" ? "min-h-0" : "")}
          >
            {scene === "url" ? <UrlScene typed={typed} ready={urlReady} /> : null}
            {scene === "reports" ? (
              <ReportsScene progress={auditDemoSceneProgress(elapsed, "reports")} />
            ) : null}
            {scene === "scan" ? (
              <ScanScene progress={auditDemoSceneProgress(elapsed, "scan")} />
            ) : null}
            {scene === "results" ? <ResultsScene elapsed={elapsed} /> : null}
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-xs font-medium text-slate-500">
        {AUDIT_DEMO_ARO_COUNT} ARO checks · {AUDIT_DEMO_CRO_COUNT} CRO benchmarks
      </p>
    </div>
  );
}
